import React, { useState, useEffect } from "react";
import { Container, Button, Alert as BsAlert, Spinner as BsSpinner } from "react-bootstrap";
import { BaseModal } from "react-epharma-components/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCheckCircle, faExclamationTriangle, faCopy } from "@fortawesome/free-solid-svg-icons";
import Helpers from "@helpers";
import apiClient from "@services/apiClient";

const Alert = BsAlert as unknown as React.ComponentType<any>;
const Spinner = BsSpinner as unknown as React.ComponentType<any>;

interface IModalResetPassword {
  show: boolean;
  setShow: (value: boolean) => void;
  userCpf: string;
  userName?: string;
}

export function ModalResetPassword(props: IModalResetPassword) {
  const { show, setShow, userCpf, userName } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setGeneratedPassword(null);
      setErrorMsg(null);
      setIsLoading(false);
    }
  }, [show, userCpf]);

  const handleReset = async () => {
    setIsLoading(true);
    setErrorMsg(null);

    try {
      const url = `${Helpers.endpoints.resetPassword}/${userCpf}`;
      const response = await apiClient.post(url, {}, {
        headers: { "X-User-Password": Helpers.apiSecurity.secretKey },
      });

      if (response.data && response.data.data && response.data.data.temporaryCode) {
        const code = response.data.data.temporaryCode;
        setGeneratedPassword(`${Helpers.apiSecurity.defaultResetPasswordPrefix}${code}`);
      } else {
        setErrorMsg("Ocorreu um erro inesperado. O código não foi retornado.");
      }
    } catch {
      setErrorMsg("Erro ao resetar senha. Verifique se o usuário existe ou tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedPassword) {
      navigator.clipboard.writeText(generatedPassword);
    }
  };

  return (
    <BaseModal
      name="ResetPasswordModal"
      size="lg"
      show={show}
      title={
        (
          <span>
            <FontAwesomeIcon icon={faKey} className="me-2" /> Resetar Senha do Usuário
          </span>
        ) as any
      }
      content={
        <Container className="p-4 text-center">
          {!generatedPassword && !isLoading && (
            <div>
              <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
              <h5>Confirmação de Reset</h5>
              <p className="mb-4">
                Deseja resetar a senha do usuário <strong>{userName}</strong> (CPF: {userCpf})?
                <br />
                <small className="text-muted">Isso invalidará a senha atual imediatamente.</small>
              </p>

              {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

              <div className="d-flex justify-content-center">
                <Button variant="secondary" className="mr-1" onClick={() => setShow(false)}>
                  Cancelar
                </Button>
                <Button variant="danger" onClick={handleReset}>
                  Confirmar Reset
                </Button>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Gerando nova senha temporária...</p>
            </div>
          )}

          {generatedPassword && (
            <div>
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h4 className="text-success">Senha Resetada com Sucesso!</h4>
              <p>Informe a seguinte senha temporária ao usuário:</p>

              <div className="bg-light p-4 rounded border my-3 d-flex align-items-center justify-content-center flex-column">
                <span className="display-6 fw-bold text-dark user-select-all" style={{ letterSpacing: "2px" }}>
                  {generatedPassword}
                </span>
                <Button variant="link" size="sm" onClick={copyToClipboard} className="mt-2 text-decoration-none">
                  <FontAwesomeIcon icon={faCopy} /> Copiar Senha
                </Button>
              </div>

              <Alert variant="info" className="text-start">
                <small>
                  <strong>Nota:</strong> Esta é uma senha temporária. O usuário deverá alterá-la no próximo login.
                </small>
              </Alert>

              <div className="d-flex justify-content-end mt-4">
                <Button variant="success" onClick={() => setShow(false)}>
                  Concluir
                </Button>
              </div>
            </div>
          )}
        </Container>
      }
      callbackAfterClose={() => setShow(!show)}
    />
  );
}
